export type TipoMovimiento = 'aporte' | 'ingreso' | 'gasto' | 'compra';
export type EstadoInversion = 'activo' | 'retirado';
export type EstadoOperativo = 'funcionando' | 'en_reparacion' | 'fuera_de_servicio';

export interface Usuario {
  id: string;
  email: string;
  nombre: string;
  activo: boolean;
  roles: string[];
  permisos: string[];
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  usuario: Usuario;
}

export interface Categoria {
  id: string;
  nombre: string;
  tipoMovimientoPermitido: TipoMovimiento | null;
  padreId: string | null;
  orden: number;
  subcategorias?: Categoria[];
}

export interface Insumo {
  id: string;
  nombre: string;
  unidad: string;
  precioOverride: string | null;
  nota: string | null;
  categoriaId: string | null;
  categoria?: { id: string; nombre: string } | null;
  conStock: boolean;
  precioActual?: number;
  stockActual?: number | null;
  compradoTotal?: number | null;
  consumidoTotal?: number | null;
}

export interface EquipoElectrico {
  id: string;
  nombre: string;
  potenciaW: string;
  activo: boolean;
  nota: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Movimiento {
  id: string;
  fecha: string;
  tipo: TipoMovimiento;
  categoriaId: string | null;
  subcategoriaId: string | null;
  insumoId: string | null;
  cantidad: string | null;
  unidad: string | null;
  descripcion: string | null;
  metodoPago: string | null;
  monto: string;
  createdAt: string;
  categoria?: { id: string; nombre: string } | null;
  subcategoria?: { id: string; nombre: string } | null;
  insumo?: { id: string; nombre: string; unidad: string } | null;
}

export interface Inversion {
  id: string;
  nombre: string;
  fecha: string;
  valor: string;
  mesesVidaUtil: number;
  depreciacionAcumulada?: number;
  valorEnLibros?: number;
  totalmenteDepreciada?: boolean;
  mesesTranscurridos?: number;
  valorResidual?: string | null;
  fechaInicioDepreciacion?: string | null;
  nota: string | null;
  estado: EstadoInversion;
  esEquipo: boolean;
  marca: string | null;
  modelo: string | null;
  serial: string | null;
  ubicacion: string | null;
  estadoOperativo: EstadoOperativo | null;
  depreciacionMensual: number;
}

export interface ResultadoCalculoProduccion {
  arepasNetas: number;
  paquetes: number;
  costoVariablePaq: number;
  costoFijoPaq: number;
  costoTotalPaq: number;
  costoVariableLote: number;
  costoFijoLote: number;
  margenPct: number;
  gananciaPaq: number;
  ingresoTotal: number;
  costoTotalLote: number;
  gananciaTotal: number;
  desglose: {
    maizCOP: number;
    gasCOP: number;
    aguaCOP: number;
    luzCOP: number;
    bolsasCOP: number;
    manoObraCOP: number;
  };
}

export interface RespuestaCalcular {
  inputs: Record<string, unknown>;
  precios: { maiz: number; gas: number; bolsa: number; agua: number; luz: number; manoObra: number };
  costosFijos: {
    depreciacionMensual: number;
    costosFijosAdicionalesMensuales: number;
    paquetesPorMes: number;
    margenObjetivo: number;
  };
  resultado: ResultadoCalculoProduccion;
}

export interface SnapshotInsumoLote {
  id: string;
  loteId: string;
  insumoId: string;
  precioUsado: string;
  cantidad: string;
  unidad: string;
  insumo?: { id: string; nombre: string; unidad: string };
}

export interface Lote {
  id: string;
  fecha: string;
  kilosMaiz: string;
  arepasBrutas: number;
  arepasMerma: number;
  arepasPorPaquete: number;
  aguaL: string;
  luzH: string;
  luzKWh: string;
  gasLb: string;
  bolsas: number;
  personas: number;
  horasTrabajo: string;
  precioVentaPaq: string;
  arepasNetas: number;
  paquetes: number;
  costoVariablePaq: string;
  costoFijoPaq: string;
  costoTotalPaq: string;
  costoVariableLote: string;
  costoFijoLote: string;
  margenPct: string;
  gananciaPaq: string;
  ingresoTotal: string;
  costoTotalLote: string;
  gananciaTotal: string;
  notas: string | null;
  createdAt: string;
  snapshots?: SnapshotInsumoLote[];
}

export interface DistribucionLote {
  ingreso: number;
  manoObra: number;
  reinversion: number;
  servicios: number;
  costosFijos: number;
  ganancia: number;
}

export interface InversionDetalleDepreciacion {
  id: string;
  nombre: string;
  valor: number;
  mesesVidaUtil: number;
  mesesTranscurridos: number;
  depreciacionMensual: number;
  depreciacionAcumulada: number;
  valorEnLibros: number;
  totalmenteDepreciada: boolean;
}

export interface InversionAcumulada {
  totalInversiones: number;
  totalAportes: number;
  depreciacionMensual: number;
  inversiones: InversionDetalleDepreciacion[];
}

export interface PeriodoResumen {
  periodo: string;
  ingresos: number;
  gastos: number;
  compras: number;
  aportes: number;
  utilidad: number;
  paquetesProducidos: number;
  ingresoProduccion: number;
}

export interface MesConYTD extends PeriodoResumen {
  ingresosYTD: number;
  gastosYTD: number;
  utilidadYTD: number;
  margenPct: number;
}

export interface UtilidadProduccionMes {
  mes: number;
  periodo: string;
  paquetes: number;
  ingresoProduccion: number;
  costoProduccion: number;
  gananciaProduccion: number;
  margenPct: number;
}

export interface PuntoEquilibrio {
  paquetesEquilibrioMes: number | null;
  precioPromedio: number;
  costoVariablePromedio: number;
  costosFijosMensuales: number;
  depreciacionMensual: number;
  otrosFijosMensuales: number;
  contribucionPaq: number;
  paquetesEstimadosMes: number;
  paquetesHistoricos: number;
}

export interface GastoPorInsumo {
  insumoId: string | null;
  insumoNombre: string;
  unidad: string | null;
  totalCantidad: number;
  totalMonto: number;
  conteo: number;
}

export interface GastosPorInsumoResp {
  items: GastoPorInsumo[];
  total: number;
}

export interface ManoObraPeriodo {
  periodo: string;
  totalHoras: number;
  totalMonto: number;
  conteo: number;
}

export interface ManoObraResp {
  agrupar: 'dia' | 'semana' | 'mes';
  periodos: ManoObraPeriodo[];
  total: number;
  totalHoras: number;
}

export interface ParametrosGlobales {
  id: string;
  margenObjetivo: string; // Decimal — llega como string de Prisma
  paquetesMesEstimados: number;
  arriendoMensual: string;
  serviciosMensual: string;
  internetMensual: string;
  otrosFijosMensual: string;
  notaCostosFijos: string | null;
  updatedAt: string;
}

export interface Rol {
  id: string;
  nombre: string;
  descripcion: string | null;
  permisos: { id: string; clave: string; descripcion: string | null }[];
}

export interface Permiso {
  id: string;
  clave: string;
  descripcion: string | null;
}

// ===================== BITÁCORA =====================

export type FaseEstandar =
  | 'coccion_maiz'
  | 'remojo'
  | 'lavado'
  | 'escurrido'
  | 'molienda'
  | 'amasado'
  | 'armado'
  | 'asado'
  | 'reposo'
  | 'empaque'
  | 'venta';

export type CalidadCualitativa = 'ok' | 'duro' | 'blando' | 'chicluda' | 'inconsistente';

export interface BitacoraFase {
  id: string;
  bitacoraId: string;
  faseEstandar: FaseEstandar | null;
  nombrePersonalizado: string | null;
  orden: number;
  horaInicio: string | null;
  horaFin: string | null;
  responsable: string | null;
  observaciones: string | null;
  novedades: string | null;
}

export interface BitacoraPersonal {
  id: string;
  bitacoraId: string;
  nombre: string;
  horaEntrada: string | null;
  horaSalida: string | null;
  rol: string | null;
}

export interface BitacoraPeso {
  id: string;
  bitacoraId: string;
  orden: number;
  pesoG: string;
  momento: string | null;
}

export interface Evidencia {
  id: string;
  bitacoraId: string;
  faseId: string | null;
  cloudinaryId: string;
  url: string;
  descripcion: string | null;
  createdAt: string;
}

export interface BitacoraLote {
  id: string;
  fecha: string;
  titulo: string | null;
  loteId: string | null;
  objetivoPrueba: string | null;
  calidadMasa: CalidadCualitativa | null;
  calidadCoccion: CalidadCualitativa | null;
  arepasDefectuosas: number;
  arepasMuestra: number;
  arepasVendidas: number;
  conclusiones: string | null;
  createdAt: string;
  updatedAt: string;
  lote?: Lote | null;
  fases: BitacoraFase[];
  personal: BitacoraPersonal[];
  pesos: BitacoraPeso[];
  evidencias: Evidencia[];
}

export const FASES_ESTANDAR_LABEL: Record<FaseEstandar, string> = {
  coccion_maiz: 'Cocción del maíz',
  remojo: 'Remojo',
  lavado: 'Lavado',
  escurrido: 'Escurrido',
  molienda: 'Molienda',
  amasado: 'Amasado',
  armado: 'Armado',
  asado: 'Asado',
  reposo: 'Reposo',
  empaque: 'Empaque',
  venta: 'Venta',
};

export const CALIDAD_LABEL: Record<CalidadCualitativa, string> = {
  ok: 'OK',
  duro: 'Duro',
  blando: 'Blando',
  chicluda: 'Chicluda',
  inconsistente: 'Inconsistente',
};

// ===================== NUEVOS REPORTES =====================

export interface ConciliacionPeriodo {
  periodo: string;
  utilidadContable: number;
  utilidadProduccion: number;
  diferencia: number;
  ingresosContables: number;
  ingresosProduccion: number;
  gastosContables: number;
  costoProduccion: number;
  paquetes: number;
}

export interface RendimientoProduccionItem {
  periodo: string;
  loteId: string;
  fecha: string;
  kilosMaiz: number;
  arepasNetas: number;
  paquetes: number;
  rendimientoArepasPorKg: number;
  rendimientoPaquetesPorKg: number;
}

export interface RendimientoHistoricoResp {
  items: RendimientoProduccionItem[];
  promedioArepasPorKg: number;
}

export interface ValorInventarioItem {
  insumoId: string;
  insumoNombre: string;
  unidad: string;
  stockActual: number;
  precioUnitario: number;
  valorTotalCOP: number;
  tienePrecio: boolean;
}

export interface ValorInventarioResp {
  items: ValorInventarioItem[];
  totalCOP: number;
  sinPrecio: string[];
}

export interface PreciosAdvertenciasResp {
  valores: {
    maiz: number;
    gas: number;
    bolsa: number;
    agua: number;
    luz: number;
    manoObra: number;
  };
  advertencias: string[];
}
